import { useMutation } from '@tanstack/react-query';
import { legacy } from '@/shared/api/client';
import {
  MIN_BILL_REMAINDER,
  computeAwardFromPurchase,
  previewOperatorBill,
} from '@/shared/lib/loyalty-engine';
import {
  resolvePreviewChannel,
  detectOperatorProgram,
  isProductRuleTipo,
  type OperatorProgram,
} from '../model/operator-rules';
import type { OperatorSaleDetail } from '../model/operator-payload';
import type { Passbook } from '@/shared/types';

function trunc2(n: number): number {
  return Math.trunc(n * 100) / 100;
}

/** Desconto em R$ + líquido — mesma regra do loyalty-engine (prévia simulate de selos). */
export function applySimulateBillDiscount(purchase: number, billDiscount?: number) {
  const rawBill =
    billDiscount != null && Number.isFinite(Number(billDiscount))
      ? Math.max(0, Number(billDiscount))
      : 0;
  if (purchase <= 0) {
    return { discount: rawBill, netValue: 0 };
  }
  const maxDiscount = Math.max(0, trunc2(purchase - MIN_BILL_REMAINDER));
  const discount = Math.min(rawBill, maxDiscount);
  // Cap atingido: líquido é exatamente o piso (evita float 250−249.99 → trunc 0).
  if (discount > 0 && discount >= maxDiscount) {
    return { discount, netValue: MIN_BILL_REMAINDER };
  }
  return { discount, netValue: Math.max(0, trunc2(purchase - discount)) };
}

/**
 * Vale em R$ cujo líquido não gera selo
 * (conta zerada ou abaixo da regra, ex. 300−250=50 com regra 250):
 * não chamar simulate/addPoints — só resgate, awardUnits = 0.
 * Vale com ou sem usesProductValueFieldAsDiscount (a flag só muda de onde sai o R$:
 * value ou price). Sem a flag, regra-por-produto fica de fora: os selos vêm dos SKUs,
 * o líquido não diz nada — o simulate decide.
 */
export function shouldSkipSimulateForFullDiscount(
  passbook: Passbook,
  discount: number,
  netValue: number,
): boolean {
  if (discount <= 0) return false;
  if (!passbook.usesProductValueFieldAsDiscount && isProductRuleTipo(passbook.regraCartao?.tipo)) {
    return false;
  }
  if (netValue <= MIN_BILL_REMAINDER) return true;
  return computeAwardFromPurchase(passbook, netValue) <= 0;
}

const OPERATOR_PROGRAMS = new Set<OperatorProgram>([
  'cashback',
  'points',
  'stamps',
  'balance',
  'giftcard',
]);

function asOperatorProgram(value: string | undefined, fallback: OperatorProgram): OperatorProgram {
  if (value && OPERATOR_PROGRAMS.has(value as OperatorProgram)) return value as OperatorProgram;
  return fallback;
}

function headers(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

export interface OperatorPreviewResult {
  program: OperatorProgram;
  purchaseValue: number;
  discount: number;
  netValue: number;
  earnBase: number;
  earnBaseMode: string;
  pointsEarned: number;
  cashbackEarned: number;
  stampsEarned: number;
  awardUnits: number;
  message: string;
  resgateNaoPontua?: boolean;
  source: 'bff' | 'simulate' | 'local';
}

/**
 * Prévia da conta.
 * Pontos/cashback: calcula no client com `loyalty-engine` (mesmo código do BFF) —
 * o passbook já está em contexto; evita round-trip e falhas de mock/rede.
 * Selos / regra-por-produto: continua no simulate da API.
 */
export function useOperatorPreview(idPassbook: string, passbook: Passbook) {
  return useMutation({
    mutationFn: async (p: {
      uniqueId?: string;
      purchaseValue: number;
      redemptionAmount?: number;
      /** Desconto em R$ (catálogo redemptionType=discount). */
      billDiscount?: number;
      productRedemption?: boolean;
      details?: OperatorSaleDetail[];
      balance?: number;
    }): Promise<OperatorPreviewResult> => {
      const channel = resolvePreviewChannel(passbook);

      // Giftcard/saldo: mesma regra de desconto do engine (catálogo discount ≠ custo em saldo).
      if (channel === 'none') {
        const result = previewOperatorBill({
          passbook,
          purchaseValue: p.purchaseValue,
          redemptionAmount: p.redemptionAmount ?? 0,
          billDiscount: p.billDiscount,
          productRedemption: Boolean(p.productRedemption),
          balance: p.balance,
        });
        return {
          ...result,
          program: asOperatorProgram(
            result.program,
            passbook.hasGiftcard ? 'giftcard' : 'balance',
          ),
          source: 'local',
        };
      }

      if (channel === 'bff') {
        const result = previewOperatorBill({
          passbook,
          purchaseValue: p.purchaseValue,
          redemptionAmount: p.redemptionAmount ?? 0,
          billDiscount: p.billDiscount,
          productRedemption: Boolean(p.productRedemption),
          balance: p.balance,
        });
        return {
          ...result,
          program: asOperatorProgram(result.program, detectOperatorProgram(passbook)),
          source: 'bff',
        };
      }

      const details = (p.details ?? []).filter((d) => d.count > 0 && d.sku);
      const purchase = p.purchaseValue;
      const { discount, netValue } = applySimulateBillDiscount(purchase, p.billDiscount);
      // Vale cujo líquido não gera selo: legado rejeita simulate com 0 — só resgate.
      if (shouldSkipSimulateForFullDiscount(passbook, discount, netValue)) {
        return {
          program: detectOperatorProgram(passbook),
          purchaseValue: purchase,
          discount,
          netValue,
          earnBase: 0,
          earnBaseMode: 'none',
          pointsEarned: 0,
          cashbackEarned: 0,
          stampsEarned: 0,
          awardUnits: 0,
          message: 'Somente resgate — sem lançamento nesta conta',
          source: 'local',
        };
      }
      // Com voucher discount: ganho sobre o líquido (remanescente), como no loyalty-engine.
      const simulateValue = discount > 0 ? netValue : purchase;
      const sim = await legacy.post<{
        status?: string;
        mensagem?: string;
        programa?: string;
        valor?: number;
      }>(
        '/transaction/simulate/addPoints',
        {
          id_passbook: idPassbook,
          uniqueId: p.uniqueId,
          value: simulateValue,
          source: 'add_points',
          ...(details.length > 0 ? { details } : {}),
        },
        headers(idPassbook),
      );
      const award = Number(sim.valor) || 0;
      return {
        program: asOperatorProgram(sim.programa, 'stamps'),
        purchaseValue: purchase,
        discount,
        netValue,
        earnBase: discount > 0 ? netValue : purchase,
        earnBaseMode: discount > 0 ? 'net' : 'none',
        pointsEarned: 0,
        cashbackEarned: 0,
        stampsEarned: award,
        awardUnits: award,
        message: sim.mensagem ?? '',
        source: 'simulate',
      };
    },
  });
}
